import { useEffect, useRef } from 'react';
import { useNode } from '@craftjs/core';
import { Icon } from '@frontend/icons';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { mergeClasses } from '@frontend/design-system';
import { emailAssistantGradientStyling } from '../../styles';
import { useComposerSection } from '../hooks/use-composer-section';
import { FloatingToolbar } from './floating-toolbar';

type Props = CommonHTMLAttributes & {
  children: React.ReactNode;
  disableCopy?: boolean;
  disableDelete?: boolean;
  disableDrag?: boolean;
  openAssistant?: () => void;
};

// TODO: Add tracking id
export const SectionBase = ({
  children,
  className,
  disableCopy = false,
  disableDelete = false,
  disableDrag = false,
  onClick,
  openAssistant,
  ...rest
}: Props) => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const { copySection, deleteSection, id, isSelected, isValidationDirty, flushSectionValidation, validationErrors } =
    useComposerSection();
  const wasSelected = useRef(isSelected);

  useEffect(() => {
    const wasPreviouslySelected = wasSelected.current;
    wasSelected.current = isSelected;

    if (!wasPreviouslySelected || isSelected || !isValidationDirty) return;
    void flushSectionValidation();
  }, [flushSectionValidation, isSelected, isValidationDirty]);

  const hasValidationErrors = validationErrors.length > 0;
  const borderColor = hasValidationErrors
    ? theme.colors.critical50
    : isSelected
    ? theme.colors.primary50
    : theme.colors.neutral20;

  return (
    <section
      id={id}
      ref={(ref) => ref && connect(ref)}
      className={mergeClasses('composer-section', isSelected ? 'selected' : 'unselected', className)}
      css={{
        background: theme.colors.neutral5,
        borderColor: isSelected ? borderColor + ' !important' : borderColor, // !important overrides border color changes from the adjacent sibling selector when the section is selected
        borderStyle: 'solid',
        borderWidth: 1,
        boxShadow: isSelected ? `0 0 0 1px ${borderColor}` : undefined,
        display: 'flex',
        justifyContent: 'center',
        justifySelf: 'center',
        marginTop: -1,
        maxWidth: 1048,
        minHeight: theme.spacing(4),
        position: 'relative',
        transition: 'max-width 0.2s ease-in-out, border-color 150ms ease, box-shadow 150ms ease',
        width: '100%',
        zIndex: isSelected ? 1 : undefined,
        '& + .composer-section': hasValidationErrors && {
          borderTopColor: theme.colors.critical50,
        },
        '&:first-of-type': {
          marginTop: 0,
        },
      }}
      onClick={(event) => {
        event.stopPropagation(); // Prevents the section from closing its own settings panel when clicked
        onClick?.(event);
      }}
      {...rest}
    >
      {!disableDrag && (
        <FloatingToolbar position='left-top'>
          <FloatingToolbar.Button
            ref={(ref) => ref && drag(ref)}
            css={{
              cursor: 'grab',
              padding: 0,
            }}
            variant='primary'
          >
            <Icon name='dragand-drop-handle' />
          </FloatingToolbar.Button>
        </FloatingToolbar>
      )}
      {(!disableCopy || !disableDelete || openAssistant) && (
        <FloatingToolbar position='top-right'>
          {openAssistant && (
            <FloatingToolbar.Button onClick={openAssistant} css={emailAssistantGradientStyling}>
              <Icon name='aiassistant-small' />
            </FloatingToolbar.Button>
          )}
          {!disableCopy && (
            <FloatingToolbar.Button onClick={copySection}>
              <Icon name='copy-small' />
            </FloatingToolbar.Button>
          )}
          {!disableDelete && (
            <FloatingToolbar.Button onClick={deleteSection}>
              <Icon name='trash-small' />
            </FloatingToolbar.Button>
          )}
        </FloatingToolbar>
      )}
      {children}
    </section>
  );
};

SectionBase.Toolbar = FloatingToolbar;
